import type { Theme } from "@mui/material";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { useState } from "react";
import z from "zod";

import { DIALOG_WIDTH } from "../../constants";
import { CloseDialogHandler } from "../../contexts/DialogContext";
import { Schemas } from "../../schemas";
import { useUpdateAlert } from "../../hooks/mutation/update/useUpdateAlert";
import { useAlert } from "../../hooks/query/useAlert";
import { useIntegration } from "../../hooks/query/useIntegration";
import { useAlertsFromIntegration } from "../../hooks/query/useAlertsFromIntegration";
import { IntegrationPicker } from "../IntegrationPicker";
import StandardDialogActions from "./StandardDialogActions";
import StandardDialogTitle from "./StandardDialogTitle";
import { Integration } from "../../types";

const schema = Schemas.alert.omit({ id: true });
type AlertFormValues = z.input<typeof schema>;

const EditAlertDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
  alertId: string;
}> = ({ onDialogClosed, alertId }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );

  const alertQuery = useAlert(alertId);
  const integrationQuery = useIntegration(
    alertQuery.data?.integrationId ?? null,
  );

  const { mutateAsync: updateAlert, isPending: isUpdating } = useUpdateAlert();

  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [isFormReady, setIsFormReady] = React.useState<boolean>(false);
  const [integration, setIntegration] = useState<Integration>();

  const { data: alertsFromProvider = [], isLoading } =
    useAlertsFromIntegration(integration);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AlertFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      integrationId: "",
      foreignKeyValue: "",
    },
    mode: "onBlur",
  });

  React.useEffect(() => {
    setIsFormReady(false);
    setIntegration(undefined);
  }, [alertId]);

  React.useEffect(() => {
    const alert = alertQuery.data;
    if (!alert) {return;}

    reset({
      name: alert.name,
      description: alert.description ?? "",
      integrationId: alert.integrationId,
      foreignKeyValue: alert.foreignKeyValue,
    });
    setIsFormReady(true);
  }, [alertQuery.data, reset]);

  React.useEffect(() => {
    const integrationData = integrationQuery.data;
    if (!integrationData) {return;}
    setIntegration(integrationData);
  }, [integrationQuery.data]);

  const onSubmit = async (data: AlertFormValues) => {
    try {
      setSubmitError(null);
      await updateAlert({ alertId, alert: schema.parse(data) });
      onDialogClosed({ message: "Alert updated!", severity: "success" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onClose = () => {
    if (isUpdating) {return;}
    setSubmitError(null);
    setIntegration(undefined);
    reset({
      name: "",
      description: "",
      integrationId: "",
      foreignKeyValue: "",
    });
    onDialogClosed();
  };

  return (
    <Dialog
      open={true}
      key={alertId}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{
        sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg },
      }}
    >
      <StandardDialogTitle
        title={"Edit Alert"}
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <Box
        component="form"
        id="edit-alert-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Create a alert and optionally declare its dependencies.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            {alertQuery.isError && (
              <FormHelperText error sx={{ fontSize: 13, m: 0, width: "100%" }}>
                {alertQuery.error instanceof Error
                  ? alertQuery.error.message
                  : "Could not load alert."}
              </FormHelperText>
            )}

            {!alertQuery.isError && !isFormReady && (
              <Box
                sx={{
                  width: "100%",
                  minHeight: 220,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={22} />
              </Box>
            )}

            <Stack
              spacing={2.25}
              sx={{ width: "100%", display: isFormReady ? "flex" : "none" }}
            >
              <FormControl>
                <FormLabel>Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. Payments Service"
                  error={!!errors.name}
                  {...register("name", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.name}>
                  {errors.name?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl error={!!errors.integrationId}>
                <FormLabel>Integration Id</FormLabel>

                <Controller
                  name="integrationId"
                  control={control}
                  render={({ field }) => (
                    <IntegrationPicker
                      value={field.value ?? ""}
                      onChange={(e) => {
                        field.onChange(e.id);
                        setIntegration(e);
                        if (submitError) {
                          setSubmitError(null);
                        }
                      }}
                    />
                  )}
                />

                <FormHelperText>
                  {errors.integrationId?.message ?? " "}
                </FormHelperText>
              </FormControl>

              {!integration && (
                <FormControl>
                  <FormLabel>Custom Alert ID</FormLabel>
                  <TextField
                    size="small"
                    placeholder="The key that identify the alert in a unique way"
                    error={!!errors.foreignKeyValue}
                    {...register("foreignKeyValue", {
                      onChange: () => submitError && setSubmitError(null),
                    })}
                  />
                  <FormHelperText error={!!errors.foreignKeyValue}>
                    {errors.foreignKeyValue?.message ?? " "}
                  </FormHelperText>
                </FormControl>
              )}

              {integration && (
                <FormControl>
                  <FormLabel>Alert</FormLabel>
                  {alertsFromProvider.length > 0 &&
                    integration.system === true && (
                      <Select
                        error={!!errors.foreignKeyValue}
                        {...register("foreignKeyValue", {
                          onChange: () => submitError && setSubmitError(null),
                        })}
                      >
                        {alertsFromProvider.map((a) => (
                          <MenuItem key={a.id} value={a.label}>
                            <ListItemText primary={a.label} />
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  {alertsFromProvider.length === 0 &&
                    !isLoading &&
                    integration.system === true && (
                      <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                        No alerts found.
                      </FormHelperText>
                    )}
                  {isLoading &&
                    integration.system === true && (
                      <CircularProgress size={16} />
                    )}
                  {integration.system !== true && (
                    <TextField
                      size="small"
                      placeholder="The key that identify the alert in a unique way"
                      error={!!errors.foreignKeyValue}
                      {...register("foreignKeyValue", {
                        onChange: () => submitError && setSubmitError(null),
                      })}
                    />
                  )}
                  <FormHelperText error={!!errors.foreignKeyValue}>
                    {errors.foreignKeyValue?.message ?? " "}
                  </FormHelperText>
                </FormControl>
              )}

              <FormControl>
                <FormLabel>Description</FormLabel>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Short description of the system"
                  error={!!errors.description}
                  {...register("description", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.description}>
                  {errors.description?.message ?? " "}
                </FormHelperText>
              </FormControl>

              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText={"Save"}
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isUpdating}
          disabled={!alertQuery.data || isUpdating || !isFormReady}
        />
      </Box>
    </Dialog>
  );
};

export default EditAlertDialog;
